// Google reCAPTCHA v3 helper, ported from the employee-management-system app's
// src/lib/recaptcha.ts so both sites mint tokens the API's verifyRecaptcha
// middleware accepts.
//
// v3 is invisible: the script loads once, then grecaptcha.execute() mints a
// short-lived token for a named action, which the API checks against Google's
// siteverify endpoint. With VITE_RECAPTCHA_SITE_KEY unset this is a no-op and
// callers send no token. The API accepts that only where its own
// RECAPTCHA_ENABLED is off; where it is on (prod), a request without a token is
// refused with "Captcha verification required".

const SITE_KEY = import.meta.env.VITE_RECAPTCHA_SITE_KEY;

interface GreCaptcha {
  ready: (cb: () => void) => void;
  execute: (siteKey: string, opts: { action: string }) => Promise<string>;
}

let scriptPromise: Promise<void> | null = null;

function loadScript(): Promise<void> {
  if (!SITE_KEY || typeof document === "undefined") return Promise.resolve();
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>('script[data-recaptcha="v3"]');
    if (existing) {
      resolve();
      return;
    }
    const script = document.createElement("script");
    script.src = `https://www.google.com/recaptcha/api.js?render=${SITE_KEY}`;
    script.async = true;
    script.defer = true;
    script.dataset.recaptcha = "v3";
    script.onload = () => resolve();
    script.onerror = () => {
      // Forget the failed load so the next submit can try again, instead of
      // every later call returning the same rejected promise.
      scriptPromise = null;
      script.remove();
      reject(new Error("Failed to load reCAPTCHA"));
    };
    document.head.appendChild(script);
  });

  return scriptPromise;
}

/**
 * A reCAPTCHA v3 token for `action`, or `null` when reCAPTCHA is not configured
 * (no site key) or could not load. Never throws: the API, not this helper,
 * decides whether a request without a token is acceptable.
 */
export async function getRecaptchaToken(action: string): Promise<string | null> {
  if (!SITE_KEY) return null;
  try {
    await loadScript();
    const grecaptcha = (window as unknown as { grecaptcha?: GreCaptcha }).grecaptcha;
    if (!grecaptcha) return null;
    await new Promise<void>((resolve) => grecaptcha.ready(() => resolve()));
    return await grecaptcha.execute(SITE_KEY, { action });
  } catch {
    return null;
  }
}
